import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MapPin, Phone, Plane, Train, Route, Clock, Mountain, Compass, Star, Instagram, MessageCircle } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { OFFICE_LIST, PHONE_TEL, PHONE_DISPLAY, INSTAGRAM, pagesOfType, PAGE_BY_PATH, waLink } from "@/data/site";
import { OwnerSection } from "@/components/Owner";
import { SeoArticle } from "@/components/SeoArticle";
import { Seo, orgLd, websiteLd, allOfficesLd, faqLd } from "@/lib/seo";
import { Img } from "@/components/Img";
import { Reveal, SectionHead } from "@/components/Reveal";
import { BookingForm } from "@/components/BookingForm";
import { FinalCta } from "@/components/Cta";

const pick = (paths) => paths.map((p) => PAGE_BY_PATH[p]).filter(Boolean);

const FAQS = [
  { q: "How do I book a taxi with Lucky Taxi Service?", a: "Call or WhatsApp 096602 80530, or fill in the booking form with your pickup, drop, date and passengers. We call you back with the vehicle options and the current fare before anything is confirmed." },
  { q: "Do you provide one-way taxis between Rajasthan cities?", a: "Yes. One-way drops are arranged between Jodhpur, Jaipur, Udaipur, Jaisalmer, Bikaner, Ajmer, Pushkar, Mount Abu and other towns. The fare depends on route, vehicle and date, so we quote it for your exact trip." },
  { q: "Can you pick me up from the airport or railway station?", a: "Yes. Share your flight or train number and arrival time when booking so the driver can plan the pickup at Jaipur, Jodhpur, Udaipur or Jaisalmer." },
  { q: "Do you arrange multi-day Rajasthan tours by taxi?", a: "Yes. We arrange a car and driver for multi-day trips across Rajasthan and can suggest an itinerary. Hotels and entry tickets are booked separately unless agreed in advance." },
  { q: "How is the taxi fare decided?", a: "Taxi fares depend on route, vehicle, travel date and trip type. Contact Lucky Taxi Service for the current fare; it is confirmed with you before the trip." },
  { q: "Can I book on WhatsApp?", a: "Yes. Send your trip details to 096602 80530 on WhatsApp and we will reply with options and the fare." },
];

const SLIDES = [
  ["jodhpurFort", "Jodhpur", "/taxi-service-in-jodhpur"],
  ["jaipur", "Jaipur", "/taxi-service-in-jaipur"],
  ["udaipur", "Udaipur", "/taxi-service-in-udaipur"],
  ["jaisalmerFort", "Jaisalmer", "/taxi-service-in-jaisalmer"],
];

function HeroSlides({ idx }) {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden bg-ink" aria-hidden>
      {SLIDES.map(([k], i) => (
        <div key={k} className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <Img k={k} alt="" priority={i === 0} sizes="100vw" className={`h-full w-full object-cover ${i === idx ? "hero-kb" : ""}`} />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/65 to-ink/20" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink/70 to-transparent" />
    </div>
  );
}

function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="relative isolate overflow-hidden" aria-labelledby="hero-title" data-testid="home-hero">
      <HeroSlides idx={idx} />
      <div className="container-x flex min-h-[620px] flex-col justify-center pb-28 pt-14 lg:min-h-[calc(100vh-114px)] lg:pb-32">
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-white backdrop-blur sm:text-[12px] sm:tracking-[0.14em]">24×7 Booking · 4 Offices · All Rajasthan</span>
        <h1 id="hero-title" className="mt-6 max-w-4xl text-5xl font-bold leading-[0.98] text-white sm:text-6xl lg:text-[88px]">
          <span className="line-mask"><span>Reliable <span className="text-gold">Taxi Service</span></span></span>
          <span className="line-mask"><span style={{ animationDelay: "140ms" }}>Across <span className="italic text-gold">Rajasthan</span></span></span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-100 md:text-lg">
          Book reliable local, outstation, one-way, sightseeing and Rajasthan tour taxis from Jaipur, Jodhpur, Jaisalmer, Udaipur and across Rajasthan.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#quick-booking" className="btn-primary" data-testid="home-hero-book">Book a Taxi<ArrowUpRight className="h-4 w-4" aria-hidden /></a>
          <a href={`tel:${PHONE_TEL}`} className="btn border border-white/60 text-white hover:bg-white hover:text-ink" data-testid="home-hero-call"><Phone className="h-4 w-4" aria-hidden />Call Now</a>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-wa" data-testid="home-hero-whatsapp"><MessageCircle className="h-4 w-4" aria-hidden />WhatsApp Now</a>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-2" role="tablist" aria-label="Main office cities">
          {SLIDES.map(([k, n, p], i) => (
            <Link key={k} to={p} role="tab" aria-selected={i === idx} onMouseEnter={() => setIdx(i)} onFocus={() => setIdx(i)} className={`relative overflow-hidden rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${i === idx ? "bg-white text-ink" : "bg-white/10 text-white hover:bg-white/20"}`} data-testid={`hero-city-${n.toLowerCase()}`}>
              {n}
              {i === idx && <span key={idx} className="hero-progress absolute inset-x-0 bottom-0 h-[3px] bg-gold" aria-hidden />}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuickBooking() {
  return (
    <section id="quick-booking" className="container-x relative z-10 -mt-14 scroll-mt-32" aria-labelledby="quick-title">
      <div className="rounded-[28px] border border-line bg-white p-6 shadow-[0_40px_90px_-40px_rgba(11,31,58,0.45)] sm:p-8" data-testid="quick-booking-card">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div><p className="eyebrow">Quick booking</p><h2 id="quick-title" className="mt-2 text-3xl font-semibold">Get your custom fare</h2></div>
          <p className="max-w-sm text-[14px] text-body">No fixed rate cards — we quote the current fare for your exact trip.</p>
        </div>
        <BookingForm variant="quick" source="hero" wide />
      </div>
    </section>
  );
}

function Marquee() {
  const words = ["Jodhpur", "Jaisalmer", "Udaipur", "Jaipur", "Bikaner", "Pushkar", "Mount Abu", "Ranthambore", "Chittorgarh", "Kumbhalgarh", "Ajmer", "Mandawa"];
  const row = [...words, ...words];
  return (
    <div className="marquee overflow-hidden border-y border-line bg-dune py-5" aria-hidden="true">
      <div className="marquee-track">
        {row.map((w, i) => (
          <span key={i} className="flex items-center gap-8 px-4 font-display text-3xl italic text-ink/80 sm:text-4xl">{w}<span className="text-xl not-italic text-saffron">✦</span></span>
        ))}
      </div>
    </div>
  );
}

const SERVICE_TILES = [
  ["/services/outstation-taxi-service", "Outstation Taxi", "City-to-city cabs across Rajasthan and beyond, with the driver handling the highway.", "highway", Route],
  ["/sightseeing/rajasthan-sightseeing-taxi", "Sightseeing Taxi", "Forts, palaces, lakes and dunes at your own pace.", "amber", Compass],
  ["/services/one-way-taxi-service", "One Way Taxi", "Drop-only trips between cities.", null, ArrowRight],
  ["/services/airport-taxi-service", "Airport Taxi", "Jaipur, Jodhpur, Udaipur & Jaisalmer airports.", null, Plane],
  ["/services/railway-station-taxi-service", "Railway Station Taxi", "Pickups for early and late trains.", null, Train],
  ["/services/local-taxi-service", "Local Taxi", "In-city errands, markets, hospitals and hotels.", null, MapPin],
  ["/services/full-day-taxi-rental", "Full Day Rental", "A car and driver for the whole day.", null, Clock],
  ["/services/rajasthan-tour-taxi", "Rajasthan Tour Taxi", "Multi-city tours with one car and driver.", null, Mountain],
];

function Services() {
  const [a, b, ...rest] = SERVICE_TILES;
  return (
    <section className="container-x py-20 md:py-28" aria-labelledby="services-title" data-testid="home-services">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead id="services-title" eyebrow="Popular taxi services" title={<>Every kind of trip, <em className="text-saffron">one trusted number.</em></>} />
        <Link to="/taxi-services" className="btn-outline shrink-0" data-testid="home-all-services">All taxi services<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
      </div>
      <div className="mt-12 grid gap-4 md:grid-cols-4 md:grid-rows-[260px_260px]">
        {[a, b].map(([p, t, d, img], i) => (
          <Reveal key={p} delay={i * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : "md:col-span-2"}>
            <Link to={p} className="group relative isolate flex h-full min-h-[260px] flex-col justify-end overflow-hidden rounded-3xl p-7" data-testid={`service-tile-${p.split("/").pop()}`}>
              <Img k={img} className="absolute inset-0 -z-10 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" sizes="(max-width:768px) 100vw, 50vw" />
              <div className="absolute inset-0 -z-10 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent" />
              <h3 className="text-3xl font-semibold text-white sm:text-4xl">{t}</h3>
              <p className="mt-2 max-w-md text-[15px] text-zinc-200">{d}</p>
              <span className="absolute right-6 top-6 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-ink transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" aria-hidden /></span>
            </Link>
          </Reveal>
        ))}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-2 md:row-span-1 md:grid-cols-3">
          {rest.slice(0, 3).map(([p, t, d, , Icon], i) => (
            <Reveal key={p} delay={0.1 + i * 0.05}>
              <Link to={p} className="group flex h-full flex-col justify-between rounded-3xl border border-line bg-white p-5 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-saffron" data-testid={`service-tile-${p.split("/").pop()}`}>
                <Icon className="h-6 w-6 text-saffron" aria-hidden />
                <div className="mt-6"><h3 className="font-sans text-[16px] font-bold">{t}</h3><p className="mt-1 text-[14px] leading-snug text-body">{d}</p></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {rest.slice(3).map(([p, t, d, , Icon]) => (
          <Link key={p} to={p} className="group flex items-center gap-4 rounded-2xl bg-ink p-5 text-white transition-colors hover:bg-char" data-testid={`service-tile-${p.split("/").pop()}`}>
            <Icon className="h-6 w-6 shrink-0 text-gold" aria-hidden />
            <span><span className="block font-semibold">{t}</span><span className="text-[14px] text-zinc-300">{d}</span></span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Offices() {
  const imgs = { jodhpur: "jodhpur", jaisalmer: "camel", udaipur: "udaipur", jaipur: "jaipur" };
  return (
    <section className="bg-ink py-20 text-white md:py-28" aria-labelledby="offices-title" data-testid="home-offices">
      <div className="container-x">
        <SectionHead light id="offices-title" eyebrow="Main office locations" title="Four Rajasthan offices. One direct line." text="Book from any of our offices or call the same number from anywhere in Rajasthan." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {OFFICE_LIST.map((o, i) => (
            <Reveal key={o.key} delay={i * 0.08} className="flex flex-col">
              <Link to={o.path} className="group block overflow-hidden rounded-3xl sm:arch" data-testid={`office-card-${o.key}`}>
                <Img k={imgs[o.key]} alt={`${o.city}, Rajasthan`} className="aspect-[16/10] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105 sm:aspect-[4/5]" sizes="(max-width:640px) 100vw, 25vw" />
              </Link>
              <h3 className="mt-5 text-3xl font-semibold text-white">{o.city}</h3>
              <p className="mt-2 flex gap-2 text-[14px] leading-relaxed text-zinc-300"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />{o.address}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-[13px]">
                <Link to={o.path} className="rounded-full bg-white px-3.5 py-1.5 font-semibold text-ink hover:bg-gold" data-testid={`office-page-${o.key}`}>Taxi in {o.city}</Link>
                <a href={o.map} target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/30 px-3.5 py-1.5 font-semibold hover:border-gold hover:text-gold" data-testid={`office-map-${o.key}`}>Map</a>
                <a href={`tel:${PHONE_TEL}`} className="rounded-full border border-white/30 px-3.5 py-1.5 font-semibold hover:border-gold hover:text-gold" data-testid={`office-call-${o.key}`}>Call</a>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cities() {
  const cities = pick(["/rajasthan/jaipur", "/rajasthan/jodhpur", "/rajasthan/jaisalmer", "/rajasthan/udaipur", "/rajasthan/bikaner", "/rajasthan/pushkar", "/rajasthan/mount-abu", "/rajasthan/ranthambore", "/rajasthan/chittorgarh"]);
  return (
    <section className="py-20 md:py-28" aria-labelledby="cities-title" data-testid="home-cities">
      <div className="container-x flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead id="cities-title" eyebrow="Popular Rajasthan cities" title="From the Thar to the Aravallis." />
        <Link to="/locations" className="btn-outline shrink-0" data-testid="home-all-locations">All Rajasthan locations<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
      </div>
      <div className="container-x mt-12">
        <div className="-mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 sm:-mx-8 sm:px-8" data-lenis-prevent-wheel>
          {cities.map((c, i) => (
            <Link key={c.path} to={c.path} className="group w-[68vw] shrink-0 snap-start sm:w-[260px]" data-testid={`city-card-${c.path.split("/").pop()}`}>
              <div className="overflow-hidden arch"><Img k={c.image} alt={`${c.label}, Rajasthan`} className="aspect-[3/4] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" sizes="260px" /></div>
              <div className="mt-4 flex items-baseline justify-between"><h3 className="text-2xl font-semibold">{c.label}</h3><span className="font-display italic text-saffron">{String(i + 1).padStart(2, "0")}</span></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function RoutesAndOneWay() {
  const routes = pagesOfType("route").slice(0, 10);
  const oneway = pagesOfType("oneway");
  return (
    <section className="bg-dune py-20 md:py-28" aria-labelledby="routes-title" data-testid="home-routes">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHead id="routes-title" eyebrow="Popular routes" title="Rajasthan's most-travelled highways." />
          <ul className="mt-10 divide-y divide-ink/10 border-y border-ink/10">
            {routes.map((r) => {
              const [from, to] = r.label.replace(" Taxi", "").split(" to ");
              return (
                <li key={r.path}>
                  <Link to={r.path} className="group flex items-center justify-between py-4" data-testid={`route-row-${r.slug}`}>
                    <span className="font-display text-2xl font-semibold sm:text-3xl">{from} <span className="mx-2 text-saffron transition-[margin] duration-300 group-hover:mx-4">→</span> {to}</span>
                    <ArrowUpRight className="h-5 w-5 text-saffron transition-transform duration-300 group-hover:rotate-45" aria-hidden />
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link to="/routes" className="btn-dark mt-8" data-testid="home-all-routes">All popular routes<ArrowRight className="h-4 w-4" aria-hidden /></Link>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl bg-white p-7 lg:sticky lg:top-32">
            <span className="eyebrow">One way taxi</span>
            <h3 className="mt-3 text-3xl font-semibold">Pay for the drop, not the return.</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-body">One-way drops from every major Rajasthan city. Tell us where you are going and we quote the current one-way fare.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {oneway.map((o) => <Link key={o.path} to={o.path} className="rounded-full border border-line px-3.5 py-2 text-[14px] font-semibold hover:border-saffron hover:text-saffron" data-testid={`oneway-chip-${o.slug}`}>{o.label.replace("One Way Taxi in ", "")}</Link>)}
            </div>
            <Link to="/one-way-taxi" className="mt-6 inline-flex items-center gap-2 text-[15px] font-semibold text-saffron" data-testid="home-all-oneway">All one way taxi options <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CardGrid({ id, eyebrow, title, items, hub, hubLabel, desc }) {
  return (
    <section className="container-x py-20 md:py-28" aria-labelledby={`${id}-title`} data-testid={`home-${id}`}>
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead id={`${id}-title`} eyebrow={eyebrow} title={title} />
        <Link to={hub} className="btn-outline shrink-0" data-testid={`home-${id}-hub`}>{hubLabel}<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
      </div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p, i) => (
          <Reveal key={p.path} delay={(i % 3) * 0.06}>
            <Link to={p.path} className="group block" data-testid={`${id}-card-${p.slug}`}>
              <div className="overflow-hidden rounded-3xl"><Img k={p.image} alt={p.label} className="aspect-[4/3] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" sizes="(max-width:640px) 100vw, 33vw" /></div>
              <h3 className="mt-5 text-2xl font-semibold transition-colors group-hover:text-saffron">{p.label}</h3>
              <p className="mt-1.5 line-clamp-2 text-[15px] text-body">{p.description || desc}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const FLEET_NOTE = { sedan: "Couples and small families on city or highway trips.", ertiga: "Families who need a little more room and luggage space.", "innova-crysta": "Long tours where comfort over many hours matters.", fortuner: "Premium travel, VIP guests and special occasions.", "tempo-traveller": "Groups travelling together with luggage.", "luxury-bus": "Large groups, weddings and pilgrim tours." };

function Fleet() {
  const items = pick(Object.keys(FLEET_NOTE).map((k) => `/fleet/${k}`));
  return (
    <section className="bg-ink py-20 md:py-28" aria-labelledby="fleet-title" data-testid="home-fleet">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHead light id="fleet-title" eyebrow="Lucky Taxi fleet" title="Choose the right car for the road ahead." text="The exact vehicle and its availability are confirmed when you book." />
          <Link to="/fleet" className="btn-ghost-light shrink-0" data-testid="home-fleet-hub">View complete fleet<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
        </div>
        <div className="mt-12 grid gap-px overflow-hidden rounded-3xl bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <Link key={p.path} to={p.path} className="group bg-ink p-7 transition-colors hover:bg-char" data-testid={`fleet-card-${p.slug}`}>
              <div className="flex items-start justify-between"><h3 className="text-3xl font-semibold text-white">{p.label}</h3><ArrowUpRight className="h-5 w-5 text-gold transition-transform group-hover:rotate-45" aria-hidden /></div>
              <p className="mt-3 text-[15px] text-zinc-300">{FLEET_NOTE[p.path.split("/").pop()]}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY = [
  ["Direct owner contact", "Speak directly to Lucky Choudhary and the booking team — no middle layers."],
  ["Four local offices", "Jodhpur, Jaisalmer, Udaipur and Jaipur offices with local knowledge of roads and sights."],
  ["Fare confirmed first", "You receive the current fare for your route, vehicle and date before you travel."],
  ["Every trip type", "Local, airport, railway, one-way, round-trip, sightseeing and multi-day tours."],
  ["Rajasthan-wide", "From desert towns to hill stations, we arrange taxis across the whole state."],
  ["Easy WhatsApp booking", "Send your plan on WhatsApp and get options without long forms."],
];

function WhyAndHow() {
  const steps = [["Share your trip", "Pickup, drop, date, passengers — by form, call or WhatsApp."], ["Get your fare", "We suggest vehicles and confirm the current fare."], ["Confirm booking", "Agree on timings and pickup point."], ["Enjoy the ride", "Your driver arrives at the agreed place and time."]];
  return (
    <section className="container-x py-20 md:py-28" aria-labelledby="why-title" data-testid="home-why">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHead id="why-title" eyebrow="Why choose Lucky Taxi Service" title="Honest, local and easy to reach." />
          <div className="relative mt-10 overflow-hidden rounded-[28px]">
            <Img k="palace" className="aspect-[16/10] w-full object-cover lg:aspect-[4/5]" sizes="(max-width:1024px) 100vw, 40vw" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <ol className="grid gap-px overflow-hidden rounded-3xl border border-line bg-line sm:grid-cols-2">
            {WHY.map(([t, d], i) => (
              <li key={t} className="bg-white p-6"><span className="font-display text-4xl italic text-saffron">{String(i + 1).padStart(2, "0")}</span><h3 className="mt-3 font-sans text-[17px] font-bold">{t}</h3><p className="mt-1.5 text-[15px] text-body">{d}</p></li>
            ))}
          </ol>
          <div className="mt-14" aria-labelledby="how-title">
            <h2 id="how-title" className="text-3xl font-semibold sm:text-4xl">How booking works</h2>
            <ol className="mt-8 grid gap-6 sm:grid-cols-4">
              {steps.map(([t, d], i) => (
                <li key={t} className="relative border-t-2 border-ink pt-5"><span className="absolute -top-3 left-0 grid h-6 w-6 place-items-center rounded-full bg-saffron text-[12px] font-bold text-white">{i + 1}</span><h3 className="font-sans text-[16px] font-bold">{t}</h3><p className="mt-1 text-[14px] text-body">{d}</p></li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewsAndFaq() {
  return (
    <section className="bg-dune py-20 md:py-28" data-testid="home-reviews-faq">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5" aria-labelledby="reviews-title">
          <SectionHead id="reviews-title" eyebrow="Customer reviews" title="Real words from real travellers." />
          <div className="mt-8 rounded-3xl border border-line bg-white p-7">
            <div className="flex gap-1 text-gold" aria-hidden>{[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5" />)}</div>
            <p className="mt-4 text-[15px] leading-relaxed text-body">We only publish genuine reviews from customers who have travelled with us. Travelled with Lucky Taxi Service? We would love to hear from you.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/reviews" className="btn-dark" data-testid="home-reviews-link">Reviews page</Link>
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="home-instagram-link"><Instagram className="h-4 w-4" aria-hidden />Instagram</a>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7" aria-labelledby="home-faq-title">
          <SectionHead id="home-faq-title" eyebrow="FAQs" title="Questions travellers ask us." />
          <Accordion type="single" collapsible className="mt-8 rounded-3xl border border-line bg-white px-6">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`h${i}`} className="border-line last:border-0">
                <AccordionTrigger className="text-left font-sans text-[16px] font-semibold hover:no-underline" data-testid={`home-faq-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-body">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/faq" className="mt-6 inline-flex items-center gap-2 font-semibold text-saffron" data-testid="home-all-faq">All FAQs <ArrowRight className="h-4 w-4" aria-hidden /></Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Lucky Taxi Service | Reliable Taxi Service Across Rajasthan"
        description="Book local, outstation, one-way, sightseeing and Rajasthan tour taxis from Jodhpur, Jaisalmer, Udaipur and Jaipur. Call 096602 80530 for the current fare."
        path="/"
        image="jodhpur"
        jsonLd={[orgLd(), websiteLd(), ...allOfficesLd(), faqLd(FAQS)]}
      />
      <Hero />
      <QuickBooking />
      <SeoArticle k="home" />
      <div className="mt-16"><Marquee /></div>
      <Services />
      <Offices />
      <OwnerSection />
      <Cities />
      <RoutesAndOneWay />
      <CardGrid id="sightseeing" eyebrow="Sightseeing" title="One-day sightseeing, beautifully paced." items={pagesOfType("sightseeing").slice(0, 6)} hub="/sightseeing" hubLabel="View all sightseeing" desc="A full day with your own car and driver." />
      <Fleet />
      <CardGrid id="tours" eyebrow="Rajasthan tour packages" title="Tours you can customise, day by day." items={pick(["/tours/rajasthan-7-days-tour", "/tours/jaisalmer-desert-tour", "/tours/jodhpur-udaipur-tour", "/tours/golden-triangle-tour", "/tours/rajasthan-honeymoon-tour", "/tours/bishnoi-village-safari-tour-jodhpur"])} hub="/tours" hubLabel="All tour packages" desc="Get a custom quote for this tour." />
      <WhyAndHow />
      <ReviewsAndFaq />
      <CardGrid id="blog" eyebrow="Travel blog" title="Plan smarter Rajasthan road trips." items={pick(["/blog/rajasthan-road-trip-guide", "/blog/jodhpur-to-jaisalmer-taxi-guide", "/blog/one-way-vs-round-trip-taxi"])} hub="/blog" hubLabel="Read the blog" desc="Practical Rajasthan travel advice." />
      <section className="container-x pb-6 text-center"><p className="text-[15px] text-body">Prefer to talk? Call <a href={`tel:${PHONE_TEL}`} className="font-semibold text-saffron" data-testid="home-inline-phone"><Phone className="mr-1 inline h-4 w-4" aria-hidden />{PHONE_DISPLAY}</a></p></section>
      <FinalCta />
    </div>
  );
}

